// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * D3 acceptance criterion 30 -- annotation-label resolver.
 *
 * Pure function that joins a caller-supplied `AnnotationLabel[]` (one
 * entry per label) against the visible `ViewElement`s and returns a
 * `ResolvedLabel[]` the renderer can paint directly.
 *
 * Like the C29 highlight resolver, this is the *only* overlay-side
 * file allowed to import the engine `ViewElement` type.  The renderer
 * itself (`AnnotationLabelOverlay.tsx`) stays engine-free, keeping
 * the "no model data modification" half of the criterion trivially
 * verifiable at the file-import level for the renderer.
 *
 * The function is a pure read.  TypeScript's `ReadonlyArray`
 * declaration prevents the loop body from mutating either input.
 */

import {
  isNamedViewElement,
  ViewElement,
} from '@simlin/core/datamodel';

import { AuxRadius, ModuleHeight, StockHeight } from '../drawing/default';
import { AnnotationLabel, ResolvedLabel } from './labels';

/** Distance between the variable's outer bound and the bottom of the label pill. */
const LABEL_VERTICAL_GAP = 6;
/** Height of the label pill (matches the renderer's badgeSize result). */
const LABEL_HEIGHT = 22;

/**
 * Outer half-extent for the variable's bounding shape.  Used to
 * place the label above the variable without overlapping its edge.
 */
function outerHalfHeightFor(element: ViewElement): number {
  switch (element.type) {
    case 'stock':
      return StockHeight / 2;
    case 'module':
      return ModuleHeight / 2;
    case 'aux':
    case 'flow':
    case 'alias':
    default:
      return AuxRadius;
  }
}

/**
 * Resolve label metadata to render-ready coordinates.
 *
 * - Region-anchored labels pass through (cx = anchor.x, cy = anchor.y).
 * - Variable-anchored labels look up the variable by ident and place
 *   the label centered horizontally above its outer edge.  Idents
 *   that don't resolve to a visible named variable are dropped
 *   silently -- a stale label from outdated metadata should not
 *   crash the overlay; it should just render nothing for that ident.
 *
 * The returned array preserves the input order so callers can
 * encode z-stacking by ordering their label list (last item paints
 * on top).
 */
export function resolveLabels(
  labels: ReadonlyArray<AnnotationLabel>,
  elements: ReadonlyArray<ViewElement>,
): ResolvedLabel[] {
  if (labels.length === 0) {
    return [];
  }

  // Index visible variables by ident once, so we don't re-scan
  // `elements` for every label.  Built lazily because the common
  // case (only region anchors, or no labels at all) skips it.
  let byIdent: Map<string, ViewElement> | undefined;
  function indexElements(): Map<string, ViewElement> {
    if (byIdent === undefined) {
      byIdent = new Map();
      for (const el of elements) {
        if (isNamedViewElement(el) && el.ident !== undefined) {
          byIdent.set(el.ident, el);
        }
      }
    }
    return byIdent;
  }

  const resolved: ResolvedLabel[] = [];
  for (const label of labels) {
    if (label.anchor.kind === 'region') {
      resolved.push({
        uid: label.uid,
        cx: label.anchor.x,
        cy: label.anchor.y,
        text: label.text,
        color: label.color,
      });
      continue;
    }
    const element = indexElements().get(label.anchor.ident);
    if (element === undefined) {
      continue;
    }
    const halfH = outerHalfHeightFor(element);
    // Centered horizontally on the variable; placed above its outer
    // bound with a fixed gap.  The pill is `LABEL_HEIGHT` tall so
    // `cy` sits half that distance above the gap line.
    const cy = element.y - halfH - LABEL_VERTICAL_GAP - LABEL_HEIGHT / 2;
    resolved.push({
      uid: label.uid,
      cx: element.x,
      cy,
      text: label.text,
      color: label.color,
    });
  }
  return resolved;
}
